import { lazy, Suspense, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ADMIN_PATH, parseAdminPath } from '../admin/admin-text';
import { ReadinessBanner, useReadiness } from './ReadinessBanner';
import type { User } from '../api';
import { LiveEvents } from '../events';
import { forwardBanner } from '../phone/forward-text';
import { displayNumber } from '../phone/number';
import { alertFixSteps, alertQuestionHiddenText, ALERTS_BLOCKED_TEXT, browserOf, keepActiveTip, micFixSteps, MIC_BLOCKED_TEXT } from '../phone/browser-help';
import { alertPrompt, ringingTitle } from '../phone/alerts';
import { dialerHint, lineStatus } from '../phone/line-status';
import { Phone, type PhoneState } from '../phone/phone';
import { CallScreen } from './CallScreen';
import { Dialer } from './Dialer';
import { History } from './History';
import { BellIcon, Logo, SettingsIcon } from './icons';
import { Settings } from './Settings';
import { setPickedZone } from '../time';

// admin pages: a separate chunk, downloaded only when an admin opens them (decision 83)
const AdminApp = lazy(() => import('../admin/AdminApp'));

const BROWSER = browserOf(navigator.userAgent);
const SITE = window.location.host;

function usePhoneState(phone: Phone): PhoneState {
  return useSyncExternalStore(phone.subscribe, phone.getState);
}

export function PhoneApp({
  user: initialUser,
  onLogout,
  onSessionEnded,
  loginNote,
}: {
  user: User;
  onLogout: () => void;
  onSessionEnded: () => void;
  /** a note from the login, e.g. "only 2 backup codes left" (two-step login) */
  loginNote?: string;
}) {
  const [user, setUser] = useState(initialUser);
  // time-zone PR: every time on screen (phone and admin pages) follows the account's zone;
  // set while rendering so the first frame already uses it
  setPickedZone(user.timeZone);
  const [note, setNote] = useState(loginNote ?? null);
  const [showSettings, setShowSettings] = useState(false);
  // One Phone per logged-in user; created once and stopped on logout/unmount.
  const phoneRef = useRef<Phone | null>(null);
  if (!phoneRef.current) phoneRef.current = new Phone();
  const phone = phoneRef.current;
  const s = usePhoneState(phone);
  // PR 1b: no number / balance line and the "number ready" notice
  const { readiness, reload: reloadReadiness, closeNumber } = useReadiness();
  // PR 3b: calls paused (payment overdue): the phone doesn't start calls
  useEffect(() => {
    phone.setPaused(readiness?.paused === true);
  }, [phone, readiness?.paused]);
  // PR 2: Settings → Plan reloads its minutes meter on the same live event
  const [usageTick, setUsageTick] = useState(0);
  const [view, setView] = useState<'phone' | 'history'>('phone');
  const [dialValue, setDialValue] = useState('');
  const [micHelp, setMicHelp] = useState(false);
  const [alertHelp, setAlertHelp] = useState(false);
  const isAdmin = user.role === 'admin';
  const onAdminPath = () => parseAdminPath(window.location.pathname) !== null;
  const [admin, setAdmin] = useState(() => isAdmin && onAdminPath());

  // /admin for someone who isn't an admin: just the phone (the server refuses the admin API anyway)
  useEffect(() => {
    if (!isAdmin && onAdminPath()) history.replaceState(null, '', '/');
    const onPop = () => setAdmin(isAdmin && onAdminPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isAdmin]);
  const openAdmin = () => {
    if (!onAdminPath()) history.pushState(null, '', ADMIN_PATH);
    setAdmin(true);
  };
  const leaveAdmin = () => {
    history.pushState(null, '', '/');
    setAdmin(false);
  };

  useEffect(() => {
    phone.start();
    const live = new LiveEvents((ev) => {
      // PR 1b: the readiness line (and on reconnect, in case an event was missed)
      if (ev.type === 'readiness.changed' || ev.type === 'hello') {
        reloadReadiness();
        setUsageTick((t) => t + 1);
      }
      phone.handleLiveEvent(ev);
    }, onSessionEnded);
    live.start();
    return () => {
      live.stop();
      phone.stop();
    };
  }, [phone, onSessionEnded, reloadReadiness]);

  // Incoming call: bring the phone view forward on small screens.
  useEffect(() => {
    if (s.call && s.call.phase !== 'ended') setView('phone');
  }, [s.call?.id, s.call?.phase]);

  const st = lineStatus(s.line, s.online);
  const ringing = s.call?.phase === 'incoming';

  // a call couldn't start because the microphone is blocked: open the steps
  useEffect(() => {
    if (s.micFailedAt) setMicHelp(true);
  }, [s.micFailedAt]);

  // ringing: the tab title flashes so the tab stands out among others
  const ringingWho = ringing && s.call ? s.call.name || displayNumber(s.call.number) : null;
  useEffect(() => {
    if (!ringingWho) {
      document.title = 'CloudPhone';
      return;
    }
    let tick = 0;
    document.title = ringingTitle(tick, ringingWho);
    const t = setInterval(() => (document.title = ringingTitle(++tick, ringingWho)), 1000);
    return () => {
      clearInterval(t);
      document.title = 'CloudPhone';
    };
  }, [ringingWho]);

  const hasLine = s.line !== 'no_phone' && s.line !== 'other_tab';
  const prompt = hasLine
    ? alertPrompt({ permission: s.alerts, notNow: s.alertsNotNow, blockedHidden: s.alertsBlockedHidden, soundOn: s.soundOn })
    : 'none';

  const callBack = (number: string) => {
    setDialValue(number);
    setView('phone');
    void phone.call(number);
  };

  const liveCall = s.call && s.call.phase !== 'ended' ? s.call : null;
  return (
    <>
    {admin && (
      <Suspense fallback={<main className="boot" aria-busy="true"><span className="boot-mark" aria-hidden /><span className="sr-only">Loading admin</span></main>}>
        <AdminApp
          me={user}
          onMe={setUser}
          onExit={leaveAdmin}
          onLogout={() => {
            // the next login opens the phone, not the admin pages
            history.replaceState(null, '', '/');
            onLogout();
          }}
          callBar={
            liveCall ? (
              <div className="banner banner-warn" role="status">
                <span>
                  {liveCall.phase === 'incoming' ? 'Incoming call' : 'On a call'} with {liveCall.name || displayNumber(liveCall.number)}.
                </span>
                <button className="btn btn-small btn-primary" type="button" onClick={leaveAdmin}>
                  Back to phone
                </button>
              </div>
            ) : undefined
          }
        />
      </Suspense>
    )}
    {/* the phone stays running while the admin pages are open (calls must not drop) */}
    <div className="app" data-view={view} hidden={admin}>
      <header className="topbar">
        <Logo />
        <div className={`line line-${st.tone}`} role="status" aria-live="polite">
          <span className="line-light" aria-hidden />
          <span className="line-label">{st.label}</span>
          {st.reason && <span className="line-reason">{st.reason}</span>}
        </div>
        {(prompt === 'line' || prompt === 'sound') && (
          // in the top bar, not a banner: the first click anywhere turns sound on,
          // and a banner vanishing under the pointer would move what was clicked
          <button
            className="btn btn-small alert-pill"
            type="button"
            onClick={() => void phone.enableAlerts()}
            aria-label={prompt === 'line' ? 'Turn on call alerts' : 'Turn on sound for incoming calls'}
            title={prompt === 'line' ? 'See incoming calls even when CloudPhone isn’t the active tab' : 'Browsers keep sound off until you click on the page'}
          >
            <BellIcon />
            <span>{prompt === 'line' ? 'Turn on call alerts' : 'Turn on sound'}</span>
          </button>
        )}
        <div className="me">
          <span className="me-name" title={user.email}>
            {user.name || user.email}
          </span>
          {isAdmin && (
            <button className="btn btn-small adm-open" type="button" onClick={openAdmin}>
              Admin
            </button>
          )}
          <button className="btn btn-quiet btn-icon" type="button" onClick={() => setShowSettings(true)} aria-label="Settings" title="Settings">
            <SettingsIcon />
          </button>
          <button className="btn btn-quiet" type="button" onClick={onLogout}>
            Log out
          </button>
        </div>
      </header>

      <div className="banners">
        {note && (
          <div className="banner banner-warn" role="status">
            <span>{note}</span>
            <button className="btn btn-small" type="button" onClick={() => { setNote(null); setShowSettings(true); }}>
              Open Settings
            </button>
            <button className="btn btn-small btn-quiet" type="button" onClick={() => setNote(null)}>
              Dismiss
            </button>
          </div>
        )}
        {s.mic === 'denied' && s.line !== 'no_phone' && (
          <div className="banner banner-bad banner-mic" role={s.micFailedAt ? 'alert' : 'status'}>
            <span>{MIC_BLOCKED_TEXT}</span>
            <button className="btn btn-small" type="button" aria-expanded={micHelp} onClick={() => setMicHelp(!micHelp)}>
              {micHelp ? 'Hide steps' : 'How to fix'}
            </button>
            {micHelp && (
              <ol className="banner-steps">
                {micFixSteps(BROWSER, SITE).map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            )}
          </div>
        )}
        {s.wasAsleep && (
          <div className="banner banner-warn" role="status">
            <span>
              Your browser put this tab to sleep to save memory, so calls couldn’t ring here for a while. {keepActiveTip(BROWSER, SITE) ?? ''}
            </span>
            <button className="btn btn-small btn-quiet" type="button" onClick={() => phone.dismissAsleep()}>
              Dismiss
            </button>
          </div>
        )}
        {hasLine && <ReadinessBanner readiness={readiness} inCall={Boolean(s.call && s.call.phase !== 'ended')} onCloseNumber={closeNumber} />}
        {s.dnd && (
          <div className="banner banner-dnd" role="status">
            <span>Do Not Disturb is on. Calls go straight to voicemail.</span>
            <button className="btn btn-small" type="button" onClick={() => void phone.setDnd(false)}>
              Turn off
            </button>
          </div>
        )}
        {forwardBanner(s.forward, s.dnd) && (
          <div className="banner banner-dnd" role="status">
            <span>{forwardBanner(s.forward, s.dnd)}</span>
            <button className="btn btn-small" type="button" onClick={() => void phone.setForwarding({ mode: 'off' })}>
              Turn off
            </button>
          </div>
        )}
        {prompt === 'card' && (
          <div className="banner banner-card" role="region" aria-labelledby="alerts-card-title">
            <BellIcon />
            <div className="banner-card-text">
              <strong id="alerts-card-title">Get alerts for incoming calls?</strong>
              <span>You’ll see calls even when CloudPhone isn’t the active tab.</span>
            </div>
            <div className="banner-card-actions">
              <button className="btn btn-small btn-primary" type="button" onClick={() => void phone.enableAlerts()}>
                Turn on
              </button>
              <button className="btn btn-small btn-quiet" type="button" onClick={() => phone.dismissAlertsCard()}>
                Not now
              </button>
            </div>
          </div>
        )}
        {s.alertsQuestionHidden && s.alerts === 'default' && (
          <div className="banner banner-info" role="status">
            <span>{alertQuestionHiddenText(BROWSER, SITE)}</span>
            <button className="btn btn-small btn-quiet" type="button" onClick={() => phone.hideAlertsQuestionHelp()}>
              Dismiss
            </button>
          </div>
        )}
        {prompt === 'blocked' && (
          <div className="banner banner-warn banner-alerts" role="status">
            <span>{ALERTS_BLOCKED_TEXT}</span>
            <button className="btn btn-small" type="button" aria-expanded={alertHelp} onClick={() => setAlertHelp(!alertHelp)}>
              {alertHelp ? 'Hide steps' : 'How to fix'}
            </button>
            <button className="btn btn-small btn-quiet" type="button" onClick={() => phone.hideBlockedAlerts()}>
              Dismiss
            </button>
            {alertHelp && (
              <ol className="banner-steps">
                {alertFixSteps(BROWSER, SITE).map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            )}
          </div>
        )}
        {s.line === 'other_tab' &&
          (s.otherTabCall !== 'none' ? (
            <div className="banner banner-warn">
              <span>
                {s.otherTabCall === 'ringing'
                  ? 'Your other tab is ringing. Using this tab will stop that call.'
                  : 'Your other tab is on a call right now. Using this tab will end that call.'}
              </span>
              <button className="btn btn-small" type="button" onClick={() => phone.useHere()}>
                {s.otherTabCall === 'ringing' ? 'Stop that call and use this tab' : 'End that call and use this tab'}
              </button>
            </div>
          ) : (
            <div className="banner banner-info">
              <span>Your phone is open in another tab of this browser. Calls ring there.</span>
              <button className="btn btn-small" type="button" onClick={() => phone.useHere()}>
                Use this tab
              </button>
            </div>
          ))}
        {s.lineMessage && (s.line === 'failed' || s.line === 'error' || s.line === 'no_phone') && (
          <div className={`banner banner-${st.tone === 'bad' ? 'bad' : 'warn'}`}>{s.lineMessage}</div>
        )}
        {!s.online && s.line !== 'other_tab' && s.line !== 'no_phone' && (
          <div className="banner banner-warn" role="status">
            You’re offline, so calls can’t ring here. Check your internet connection; the phone reconnects by itself.
          </div>
        )}
        {s.elsewhere && !s.call && (
          <div className="banner banner-info" role="status">
            {s.elsewhere.phase === 'ringing'
              ? `${s.elsewhere.name || displayNumber(s.elsewhere.number)} is calling. It’s ringing on your other device${s.line === 'other_tab' ? ' or tab' : ''}.`
              : `On a call with ${s.elsewhere.name || displayNumber(s.elsewhere.number)} on another device.`}
          </div>
        )}
        {s.notice && (
          <div className="banner banner-bad" role="alert">
            <span>{s.notice}</span>
            <button className="btn btn-small btn-quiet" type="button" onClick={() => phone.dismissNotice()}>
              Dismiss
            </button>
          </div>
        )}
      </div>

      <nav className="switcher" aria-label="Sections">
        <button type="button" aria-pressed={view === 'phone'} onClick={() => setView('phone')}>
          Phone
        </button>
        <button type="button" aria-pressed={view === 'history'} onClick={() => setView('history')}>
          Calls
        </button>
      </nav>

      <main className="workspace">
        <section className="pane pane-phone" aria-label="Phone">
          {s.call ? (
            <CallScreen phone={phone} s={s} onChooseCallerId={(choice) => phone.chooseCallerId(choice)} />
          ) : (
            <Dialer
              value={dialValue}
              onChange={setDialValue}
              canCall={s.line === 'ready' && s.online}
              disabledReason={st.notReady ? dialerHint(st) : null}
              onCall={(n) => void phone.call(n)}
              callerId={s.callerId}
              onChooseCallerId={(choice) => phone.chooseCallerId(choice)}
            />
          )}
        </section>
        <section className="pane pane-history" aria-label="Recent calls">
          <History
            phone={phone}
            canCall={s.line === 'ready' && s.online && !s.call}
            onCallBack={callBack}
            severalNumbers={(s.callerId?.numbers.length ?? 0) > 1}
            numberNames={s.callerId?.names}
          />
        </section>
      </main>

      {showSettings && (
        <Settings
          user={user}
          callerId={s.callerId}
          onNumberName={(n, name) => phone.setNumberName(n, name)}
          dnd={s.dnd}
          onDnd={(on) => phone.setDnd(on)}
          forward={s.forward}
          onForward={(change) => phone.setForwarding(change)}
          alerts={s.alerts}
          onEnableAlerts={() => phone.enableAlerts()}
          relayAlways={s.relayAlways}
          relayAvailable={s.relayAvailable}
          relayOk={s.relayOk}
          onRelay={(on) => phone.setRelayAlways(on)}
          onClose={() => setShowSettings(false)}
          onSaved={setUser}
          usageKey={usageTick}
        />
      )}
    </div>
    </>
  );
}
